import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Flex,
  Heading,
  VStack,
  Text,
  Divider,
  Button,
  useDisclosure,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
  Icon,
  FormControl,
  FormLabel,

  Badge,
  Input,
  Grid,
  Portal,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  useColorMode,
  Tooltip,
} from "@chakra-ui/react";
import {
  AiOutlinePlus,
  AiOutlineCheckCircle,
  AiOutlineMenuFold,
  AiOutlineMenuUnfold,
  AiOutlineSearch,
  AiOutlineCloseCircle,
} from "react-icons/ai";
import ProjectsDropdown from "./ProjectsDropdown";
import TaskCreator from "./TaskCreator";
import { db, auth } from "../../firebase/firebase";
import { logToSystem } from "../../utils/systemLogging";
import { collection, deleteDoc, doc, getDocs, getDoc, updateDoc, setDoc, arrayUnion, onSnapshot } from "firebase/firestore";
import Gantt from "../../Gantt/Gantt";

import { FaTrash, FaEdit, FaChevronDown } from "react-icons/fa";
import { CiEdit } from "react-icons/ci";
import { MdDeleteOutline } from "react-icons/md";
import TaskCompletionChart from "./TaskCompletionChart";
import RiskManagementChart from "./RiskManagementChart";
import TaskOverviewChart from "./TaskOverviewChart";
import { IconWrapper } from "../../components/IconWrapper";
import { getThemeClasses } from "../../utils/themeUtils";
import {
  getTaskPermissions,
  getProjectScopeAccess,
  getUserProjectAccessMap,
  canSeeTaskInProject,
  filterTaskDataByAssignment,
} from "../../utils/permissions";
import { FiDownload, FiPrinter } from "react-icons/fi";
import { showToast } from "../../utils/toastUtils";

export type Channel = {
  id: string;
  name: string;
  organizationId: string;
};

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  assignedChannels?: Record<string, string[]>;
  assignedProjects?: string[];
  assignedTasks?: {
    id: string;
    name: string;
    channelId: string;
    projectId: string;
    taskId: string;
  }[];
  organizationId: string;
  permissions?: string;
};

export type TaskStatus = "Not-started" | "In-progress" | "completed" | "delay";

export type TaskDataEntry = {
  status: TaskStatus;
  risk?: "low" | "Medium" | "high";
  // Assignment is stored per row (and per subtask), not on the task-group doc.
  assignedUsers?: string[];
  createdBy?: string;
  subtasks?: TaskDataEntry[];
  // Add other fields from your task data structure if needed
};

export type Tasks = {
  id: string;
  title: string;
  description: string;
  createdAt: Date;
  createdBy?: string;
  data?: TaskDataEntry[]; // Added data field based on user's snippet
};

export type Project = {
  id: string;
  name: string;
  description?: string;
  channels: Channel[];
  tasks?: Tasks[];
};

export type FilterProject = {
  id: string;
  name: string;
  description?: string;
  channels: string[];
  assignedUsers?: string[];
  status?: string;
};

interface GanttMainProps {
  organizationId: string;
  bg: string;
  color: string;
  users: any[];
  filteredprojects: FilterProject[];
  isSidebarOpen: boolean;
  currentUser: any;
}

const GanttMain: React.FC<GanttMainProps> = ({
  organizationId,
  bg,
  color,
  users,
  currentUser,
  isSidebarOpen,
  filteredprojects,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const taskPerms = React.useMemo(
    () => getTaskPermissions(currentUser),
    [currentUser]
  );
  const { isAdmin, canViewAll, canCreate, canEdit, canDelete } = taskPerms;

  // Per-project role for whichever project is open. Project grants only ever
  // widen what the org-wide permission already allows — and never grant delete.
  const projectScope = React.useMemo(
    () => getProjectScopeAccess(currentUser, selectedProject?.id),
    [currentUser, selectedProject?.id]
  );
  const canCreateTask = canCreate || projectScope.canManageTasks;
  const canEditTask = React.useCallback(
    (task: any) => canEdit(task) || projectScope.canManageTasks,
    [canEdit, projectScope]
  );

  const showPermissionToast = () => {
    showToast({
      title: "Permission Restricted",
      description: "You do not have permission to perform this action.",
      status: "warning",
      duration: 3000,
      isClosable: true,
    });
  };

  // Log "Viewed tasks inside project"
  useEffect(() => {
    if (selectedProject && currentUser && organizationId) {
      // We need to log this action. 
      // Since GanttMain doesn't have the logActivity helper from Dashboard, we need to write to Firestore directly.
      // Or we could pass logActivity as a prop, but that requires drilling. Writing directly is cleaner here for now.

      const logProjectView = async () => {
        try {
          await logToSystem(organizationId, "actions", {
            type: "ACTIVITY",
            user: `${currentUser.firstName} ${currentUser.lastName}`,
            email: currentUser.email,
            action: `Viewed tasks inside project ${selectedProject.name}`,
            details: `User viewed tasks for project: ${selectedProject.name}`,
            status: "info",
          });
        } catch (error) {
          console.error("Error logging project view:", error);
        }
      };

      logProjectView();
    }
  }, [selectedProject, currentUser, organizationId]);
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [tasks, setTasks] = useState<Tasks[]>([]);
  const [selectedTask, setSelectedTask] = useState<Tasks | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isExpanded, setIsExpanded] = useState(true);
  const [projectLoading, setProjectLoading] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isViewingGantt, setIsViewingGantt] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  // Responsive channel tab count
  const [maxVisibleChannels, setMaxVisibleChannels] = useState(8);
  useEffect(() => {
    const computeMax = () => {
      const w = window.innerWidth;
      if (w < 640) setMaxVisibleChannels(3); // sm-
      else if (w < 1024) setMaxVisibleChannels(5); // md
      else setMaxVisibleChannels(8); // lg+
    };
    computeMax();
    window.addEventListener("resize", computeMax);
    return () => window.removeEventListener("resize", computeMax);
  }, []);

  // Dynamic fit calculation for channel tabs (no horizontal scroll)
  const channelsBarRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const [fitVisibleCount, setFitVisibleCount] = useState<number>(0);

  useEffect(() => {
    const recalc = () => {
      const container = channelsBarRef.current;
      const meas = measureRef.current;
      if (!container || !meas) return;

      const availableWidth = container.clientWidth;
      // Gather widths of each chip from measurement nodes
      const chips = Array.from(
        meas.querySelectorAll('[data-chip="true"]')
      ) as HTMLDivElement[];
      const gap = 8; // tailwind gap-2
      // Reserve space for potential More button when overflow
      const moreBtnReserve = 110; // approx px for More button
      let running = 0;
      let count = 0;
      for (let i = 0; i < chips.length; i++) {
        const chipWidth = Math.ceil(chips[i].getBoundingClientRect().width);
        const nextWidth = running === 0 ? chipWidth : running + gap + chipWidth;
        // If adding this would exceed available width, stop (leave space for More)
        if (
          nextWidth + (i < chips.length - 1 ? moreBtnReserve : 0) >
          availableWidth
        ) {
          break;
        }
        running = nextWidth;
        count++;
      }
      setFitVisibleCount(count);
    };

    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [selectedProject?.channels, selectedProject?.id, searchTerm]);

  // Calculate task completion stats
  const { totalTasks, completedTasks, remainingTasks } = useMemo(() => {
    const total = tasks.reduce((totalCount: number, task: Tasks) => {
      const mainTasks = task.data?.length || 0;
      const subtasks =
        task.data?.reduce(
          (subTotal: number, entry) => subTotal + (entry.subtasks?.length || 0),
          0
        ) || 0;
      return totalCount + mainTasks + subtasks;
    }, 0);

    const completed = tasks.reduce((completedCount: number, task: Tasks) => {
      const completedInTask =
        task.data?.reduce((taskTotal: number, entry) => {
          // Count completed main tasks
          const mainTaskCompleted = entry.status === "completed" ? 1 : 0;

          // Count completed subtasks
          const completedSubtasks =
            entry.subtasks?.filter((sub) => sub.status === "completed")
              .length || 0;

          return taskTotal + mainTaskCompleted + completedSubtasks;
        }, 0) || 0;

      return completedCount + completedInTask;
    }, 0);

    const remaining = total - completed;

    return {
      totalTasks: total,
      completedTasks: completed,
      remainingTasks: remaining,
    };
  }, [tasks]);

  // Calculate task risk stats
  const { lowRiskTasks, mediumRiskTasks, highRiskTasks } = useMemo(() => {
    const low = tasks.reduce((count: number, task: Tasks) => {
      let taskLowCount =
        task.data?.filter((entry) => entry.risk === "low").length || 0;
      taskLowCount +=
        task.data?.reduce(
          (subCount, entry) =>
            subCount +
            (entry.subtasks?.filter((sub) => sub.risk === "low").length || 0),
          0
        ) || 0;
      return count + taskLowCount;
    }, 0);

    const medium = tasks.reduce((count: number, task: Tasks) => {
      let taskMediumCount =
        task.data?.filter((entry) => entry.risk === "Medium").length || 0;
      taskMediumCount +=
        task.data?.reduce(
          (subCount, entry) =>
            subCount +
            (entry.subtasks?.filter((sub) => sub.risk === "Medium").length ||
              0),
          0
        ) || 0;
      return count + taskMediumCount;
    }, 0);

    const high = tasks.reduce((count: number, task: Tasks) => {
      let taskHighCount =
        task.data?.filter((entry) => entry.risk === "high").length || 0;
      taskHighCount +=
        task.data?.reduce(
          (subCount, entry) =>
            subCount +
            (entry.subtasks?.filter((sub) => sub.risk === "high").length || 0),
          0
        ) || 0;
      return count + taskHighCount;
    }, 0);

    return { lowRiskTasks: low, mediumRiskTasks: medium, highRiskTasks: high };
  }, [tasks]);

  // Calculate task status counts
  const {
    delayedTasks,
    notStartedTasks,
    completedStatusTasks,
    inProgressTasks,
  } = useMemo(() => {
    let delayed = 0;
    let notStarted = 0;
    let completed = 0;
    let inProgress = 0;

    tasks.forEach((task) => {
      task.data?.forEach((entry) => {
        if (entry.status === "delay") {
          delayed++;
        } else if (entry.status === "Not-started") {
          notStarted++;
        } else if (entry.status === "completed") {
          completed++;
        } else if (entry.status === "In-progress") {
          inProgress++;
        }
        entry.subtasks?.forEach((subtask) => {
          if (subtask.status === "delay") {
            delayed++;
          } else if (subtask.status === "Not-started") {
            notStarted++;
          } else if (subtask.status === "completed") {
            completed++;
          } else if (subtask.status === "In-progress") {
            inProgress++;
          }
        });
      });
    });

    return {
      delayedTasks: delayed,
      notStartedTasks: notStarted,
      completedStatusTasks: completed,
      inProgressTasks: inProgress,
    };
  }, [tasks]);

  // Filter tasks based on search term
  const filteredTasks = useMemo(() => {
    if (!searchTerm) return tasks;
    return tasks.filter(
      (task) =>
        task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [tasks, searchTerm]);

  // Task status filter (right side control)
  type StatusKey =
    | "all"
    | "delay"
    | "In-progress"
    | "Not-started"
    | "completed";
  const [statusFilter, setStatusFilter] = useState<StatusKey>("all");

  // Completed / Pending tabs (next to the search field)
  type GroupStatusKey = "all" | "completed" | "pending";
  const [groupFilter, setGroupFilter] = useState<GroupStatusKey>("all");

  // Precompute status counts per task for efficient sorting/filtering
  const tasksWithCounts = useMemo(() => {
    return filteredTasks.map((task) => {
      const counts = (task.data || []).reduce(
        (acc, entry) => {
          if (entry.status === "delay") acc.delay++;
          else if (entry.status === "completed") acc.completed++;
          else if (entry.status === "In-progress") acc["In-progress"]++;
          else if (entry.status === "Not-started") acc["Not-started"]++;
          (entry.subtasks || []).forEach((s) => {
            if (s.status === "delay") acc.delay++;
            else if (s.status === "completed") acc.completed++;
            else if (s.status === "In-progress") acc["In-progress"]++;
            else if (s.status === "Not-started") acc["Not-started"]++;
          });
          return acc;
        },
        { delay: 0, completed: 0, "In-progress": 0, "Not-started": 0 }
      );
      return { task, counts };
    });
  }, [filteredTasks]);

  // A task group is "Completed" when every (main + sub) task in it is completed;
  // "Pending" when it has any mix of completed/in-progress/not-started/delayed
  // (i.e. anything less than fully completed). Independent of statusFilter below —
  // both derive straight from tasksWithCounts rather than chaining off each other.
  const filteredByGroupStatus = useMemo(() => {
    if (groupFilter === "all") return tasksWithCounts;
    return tasksWithCounts.filter((t) => {
      const pendingCount =
        t.counts.delay + t.counts["In-progress"] + t.counts["Not-started"];
      return groupFilter === "completed"
        ? pendingCount === 0
        : pendingCount > 0;
    });
  }, [tasksWithCounts, groupFilter]);

  const filteredAndSortedByStatus = useMemo(() => {
    if (statusFilter === "all") return tasksWithCounts;
    // Strictly include only tasks that have at least one item in the selected status
    const list = tasksWithCounts
      .filter((t) => Number((t.counts as any)[statusFilter]) > 0)
      .sort(
        (a, b) =>
          Number((b.counts as any)[statusFilter]) -
          Number((a.counts as any)[statusFilter])
      );
    return list;
  }, [tasksWithCounts, statusFilter]);

  // Only one filter mechanism is ever active at a time (see the reset logic on
  // each filter's onClick/onChange handler below) — the group tabs take priority
  // when active, otherwise fall back to the status dropdown.
  const displayTasks = useMemo(() => {
    const source =
      groupFilter !== "all" ? filteredByGroupStatus : filteredAndSortedByStatus;
    return source.map((t) => t.task);
  }, [groupFilter, filteredByGroupStatus, filteredAndSortedByStatus]);

  const currentTasks = displayTasks;

  // Cards scroll container ref
  const cardsContainerRef = useRef<HTMLDivElement | null>(null);

  // Quick jump removed in paginated mode

  // Fetch projects and channels from Firestore
  useEffect(() => {
    const fetchProjects = async () => {
      setProjectLoading(true);
      const projectSnapshot = await getDocs(
        collection(db, `organization/${organizationId}/projects`)
      );

      let projectsData = await Promise.all(
        projectSnapshot.docs.map(async (projectDoc) => {
          const projectData = projectDoc.data();
          const projectId = projectDoc.id;
          const channelsSnapshot = await getDocs(
            collection(
              db,
              `organization/${organizationId}/projects/${projectDoc.id}/channels`
            )
          );

          let channels: Channel[] = channelsSnapshot.docs.map((channelDoc) => ({
            id: channelDoc.id,
            name: channelDoc.data().name,
            organizationId,
          }));

          // Only an admin-level role (org-wide or granted on this project) sees
          // every channel; editors and viewers stay limited to assigned ones.
          const scope = getProjectScopeAccess(currentUser, projectId);
          if (currentUser && !canViewAll && !scope.canViewAllChannels) {
            const assignedChannelIds =
              currentUser.assignedChannels?.[projectId] || [];
            channels = channels.filter((ch) =>
              assignedChannelIds.includes(ch.id)
            );
          }

          return {
            id: projectDoc.id,
            name: projectData.name,
            description: projectData.description || "",
            channels,
          };
        })
      );

      if (currentUser && !canViewAll) {
        const accessMap = getUserProjectAccessMap(currentUser);
        projectsData = projectsData.filter(
          (proj) =>
            currentUser.assignedProjects?.includes(proj.id) || !!accessMap[proj.id]
        );
      }

      projectsData.sort((a, b) => a.name.localeCompare(b.name));

      setProjects(projectsData);
      setProjectLoading(false);
    };

    fetchProjects();
  }, [organizationId]);

  // Fetch tasks when a channel is selected - Refactored to onSnapshot for real-time updates
  useEffect(() => {
    if (!selectedProject || !selectedChannel || !organizationId) return;

    const tasksRef = collection(
      db,
      `organization/${organizationId}/projects/${selectedProject.id}/channels/${selectedChannel.id}/tasks`
    );

    const unsubscribe = onSnapshot(
      tasksRef,
      (snapshot) => {
        const fetchedTasks: Tasks[] = snapshot.docs.map((taskDoc) => ({
          id: taskDoc.id,
          ...taskDoc.data(),
        })) as Tasks[];

        // Under a project-scoped editor/viewer grant, only tasks the user is
        // assigned to (or created) are visible. Admin-level access — org-wide
        // or granted on this project — still sees every task.
        const scope = getProjectScopeAccess(currentUser, selectedProject.id);
        const visibleTasks = scope.canViewAllTasks
          ? fetchedTasks
          : fetchedTasks.filter((task) => canSeeTaskInProject(scope, task));

        setTasks(visibleTasks);
        // Only reset pagination/search if it's the first load or channel changed
        // Actually, better to reset on selection change rather than every snapshot
      },
      (error) => {
        console.error("Error fetching tasks in real-time:", error);
        showToast({
          title: "Connection Error",
          description: "Could not establish real-time connection for tasks.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    );

    // Reset search when the selection changes
    setSearchTerm("");

    return () => unsubscribe();
  }, [selectedProject?.id, selectedChannel?.id, organizationId, currentUser]);

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;
    const targetTask = tasks.find((t) => t.id === taskToDelete);
    if (!canDelete(targetTask || undefined)) {
      showPermissionToast();
      setDeleteModalOpen(false);
      setTaskToDelete(null);
      return;
    }
    setIsDeleting(true);
    try {
      // Delete the task from Firestore
      await deleteDoc(
        doc(
          db,
          `organization/${organizationId}/projects/${selectedProject?.id}/channels/${selectedChannel?.id}/tasks/${taskToDelete}`
        )
      );

      // Update organization members to remove the task from assignedTasks
      const orgRef = doc(db, "organization", organizationId);
      const orgDoc = await getDoc(orgRef);

      if (orgDoc.exists()) {
        const orgData = orgDoc.data();
        const updatedMembers = orgData.members.map((member: any) => {
          if (member.assignedTasks) {
            return {
              ...member,
              assignedTasks: member.assignedTasks.filter(
                (task: any) => task.taskId !== taskToDelete
              ),
            };
          }
          return member;
        });

        await updateDoc(orgRef, { members: updatedMembers });
      }

      // Update individual users to remove the task from assignedTasks
      const usersRef = collection(db, "users");
      const usersSnapshot = await getDocs(usersRef);

      const updatePromises = usersSnapshot.docs.map(async (userDoc) => {
        const userData = userDoc.data();
        if (userData.assignedTasks) {
          const updatedTasks = userData.assignedTasks.filter(
            (task: any) => task.taskId !== taskToDelete
          );

          if (updatedTasks.length !== userData.assignedTasks.length) {
            await updateDoc(doc(db, "users", userDoc.id), {
              assignedTasks: updatedTasks,
            });
          }
        }
      });

      await Promise.all(updatePromises);

      // Get task title before deletion for logging
      const deletedTask = tasks.find(t => t.id === taskToDelete);
      const taskTitle = deletedTask?.title || "Unknown Task";

      // Log \"Task Group Deleted\" Activity
      try {
        const currentUserAuth = auth.currentUser;
        if (currentUserAuth) {
          const userRef = doc(db, "users", currentUserAuth.uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            const userData = userSnap.data();
            await logToSystem(organizationId, "actions", {
              type: "ACTIVITY",
              user: `${userData.firstName} ${userData.lastName}`,
              email: userData.email,
              action: "Task Management",
              details: `Deleted Task Group "${taskTitle}" from project "${selectedProject?.name}"`,
              status: "success",
            });
          }
        }
      } catch (logError) {
        console.error("Error logging task deletion:", logError);
      }

      // Update local state
      // Note: Manual state update removed. parent's onSnapshot listener handles real-time updates.
      showToast({
        title: "Task Deleted",
        description: "Task has been successfully removed.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setDeleteModalOpen(false);
      setTaskToDelete(null);
    } catch (error) {
      console.error("Error deleting task:", error);
      showToast({
        title: "Error",
        description: "Failed to delete task.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpdateTask = (task: Tasks) => {
    if (!canEditTask(task)) {
      showPermissionToast();
      return;
    }
    setSelectedTask(task);
    setIsEditing(true);
    onOpen(); // Open modal for editing
  };

  const handleTaskCreated = (newTask: {
    id: string;
    channel: string;
    title: string;
    description: string;
    createdAt: string;
  }) => {
    // Note: Manual state update removed. The onSnapshot listener in GanttMain 
    // already handles real-time updates for the task list.
    console.log("Task creation handled by real-time listener:", newTask.id);
  };

  // Export tasks to CSV with logging
  const handleExportTasks = async () => {
    if (tasks.length === 0 || !selectedProject || !selectedChannel) return;

    const headers = ["Task Name", "Description", "Created At"];
    const csvContent = [
      headers.join(","),
      ...tasks.map(task => [
        `"${task.title}"`,
        `"${task.description}"`,
        new Date(task.createdAt).toLocaleDateString()
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `tasks-${selectedProject.name}-${selectedChannel.name}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    // Log the export action
    try {
      const currentUserAuth = auth.currentUser;
      if (currentUserAuth) {
        const userRef = doc(db, "users", currentUserAuth.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          await logToSystem(organizationId, "actions", {
            type: "ACTIVITY",
            user: `${userData.firstName} ${userData.lastName}`,
            email: userData.email,
            action: "Task Management",
            details: `Exported tasks for project "${selectedProject.name}" and channel "${selectedChannel.name}" to CSV`,
            status: "success",
          });
        }
      }
    } catch (error) {
      console.error("Error logging export:", error);
    }
  };

  // Print tasks with logging
  const handlePrintTasks = async () => {
    if (tasks.length === 0 || !selectedProject || !selectedChannel) return;

    const printWindow = window.open('', '', 'height=600,width=800');
    if (!printWindow) return;

    printWindow.document.write('<html><head><title>Task Groups - ' + selectedProject.name + ' - ' + selectedChannel.name + '</title>');
    printWindow.document.write('<style>');
    printWindow.document.write('body { font-family: Arial, sans-serif; margin: 20px; }');
    printWindow.document.write('h1 { color: #333; }');
    printWindow.document.write('table { width: 100%; border-collapse: collapse; margin-top: 20px; }');
    printWindow.document.write('th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }');
    printWindow.document.write('th { background-color: #f2f2f2; font-weight: bold; }');
    printWindow.document.write('tr:nth-child(even) { background-color: #f9f9f9; }');
    printWindow.document.write('@media print { button { display: none; } }');
    printWindow.document.write('</style></head><body>');
    printWindow.document.write('<h1>Task Groups - ' + selectedProject.name + ' - ' + selectedChannel.name + '</h1>');
    printWindow.document.write('<p>Generated on: ' + new Date().toLocaleString() + '</p>');
    printWindow.document.write('<table>');
    printWindow.document.write('<thead><tr><th>Task Name</th><th>Description</th><th>Created At</th></tr></thead>');
    printWindow.document.write('<tbody>');

    tasks.forEach(task => {
      printWindow.document.write('<tr>');
      printWindow.document.write('<td>' + task.title + '</td>');
      printWindow.document.write('<td>' + task.description + '</td>');
      printWindow.document.write('<td>' + new Date(task.createdAt).toLocaleDateString() + '</td>');
      printWindow.document.write('</tr>');
    });

    printWindow.document.write('</tbody></table>');
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);

    // Log the print action
    try {
      const currentUserAuth = auth.currentUser;
      if (currentUserAuth) {
        const userRef = doc(db, "users", currentUserAuth.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          await logToSystem(organizationId, "actions", {
            type: "ACTIVITY",
            user: `${userData.firstName} ${userData.lastName}`,
            email: userData.email,
            action: "Task Management",
            details: `Printed ${tasks.length} task groups from project "${selectedProject.name}" - "${selectedChannel.name}"`,
            status: "success",
          });
        }
      }
    } catch (error) {
      console.error("Error logging print:", error);
    }
  };

  return (
    <Flex
      direction="column"
      h="100%"
      minH={0}
      className={`${isDark ? "bg-[#1A1C1F] text-white" : "bg-white text-black"}`}
    >
      {/* Main Page Title and Description */}
      {!isViewingGantt && (
        <Box className={`px-4 py-3 flex flex-col md:flex-row md:justify-between md:items-center gap-3 p-4 ${theme.headerGradient} rounded-md mb-4 border ${theme.border}`}>
          {isDark && (
            <>
              <span className={theme.headerOverlayGlow} />
              <span className={theme.headerOverlayTopLine} />
              <span className={theme.headerOverlayBottomGlow} />
              <span className={theme.headerOverlaySweep} />
            </>
          )}
          <div className="flex flex-col space-y-1">
            <span className={`text-2xl font-bold ${theme.text}`}>Tasks</span>
            <span className={`text-xs ${theme.textMuted}`}>
              View and manage tasks within your selected project and channel.
            </span>
          </div>

          <HStack className="space-x-0 items-center w-full md:w-auto">
            <FormLabel
              className="mt-1 font-bold text-sm mb-0 self-center "
              color={color}
              style={{ fontSize: "14px", fontWeight: "bold" }}
            >
              Project:
            </FormLabel>
            <Box className="w-full only-of-type:min-w-[150px]  md:min-w-[230px]">
              <ProjectsDropdown
                projectLoading={projectLoading}
                projects={projects}
                selectedProject={selectedProject}
                onProjectSelect={(project) => {
                  setSelectedProject(project);
                  const firstChannel = project?.channels?.[0] || null;
                  setSelectedChannel(firstChannel);
                  setTasks([]);
                  setSelectedTask(null);
                  setIsViewingGantt(false);
                  setSearchTerm("");
                }}
              />
            </Box>
          </HStack>
        </Box>
      )}

      {/* Top Bar for Project and Channel Selection */}
      {!isViewingGantt && (
        <Flex
          align="center"
          className={`px-6 mt-3 relative z-20 ${isDark ? "bg-[#1A1C1F] text-white border-[#2E2F33]" : "bg-white text-black border-gray-300"}`}
        >
          {selectedProject && selectedProject.channels.length > 0 ? (
            <Box className="w-full">
              <FormLabel
                className="mt-1 font-bold text mb-2 self-center pr-1"
                color={color}
                style={{ fontSize: "14px", fontWeight: "bold" }}
              >
                Workspace Channels : {selectedProject.channels.length}
              </FormLabel>
              {/* Hidden measurement container */}
              <div
                ref={measureRef}
                className="invisible absolute -z-10 h-0 overflow-hidden"
              >
                <div className="inline-flex gap-2">
                  {selectedProject.channels.map((ch) => (
                    <div
                      key={`measure-${ch.id}`}
                      data-chip="true"
                      className="whitespace-nowrap px-3.5 py-1.5 text-sm rounded-lg font-medium border border-transparent"
                    >
                      <span className="inline-block max-w-[180px] truncate align-middle">
                        {ch.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div ref={channelsBarRef} className="w-full overflow-hidden">
                <div className="inline-flex gap-2 rounded-2xl  p-1.5">
                  {(() => {
                    const total = selectedProject.channels.length;
                    const baseCap = maxVisibleChannels; // responsive baseline
                    const cap = Math.min(baseCap, fitVisibleCount);
                    const needsMore = total > cap;
                    const visibleCount = needsMore
                      ? Math.max(0, cap - 1)
                      : Math.min(cap, total);
                    const visible = selectedProject.channels.slice(
                      0,
                      visibleCount
                    );
                    return visible.map((ch) => {
                      const isActive = selectedChannel?.id === ch.id;
                      return (
                        <button
                          key={ch.id}
                          onClick={() => {
                            setSelectedChannel(ch);
                            setSelectedTask(null);
                            setIsViewingGantt(false);
                            setSearchTerm("");
                          }}
                          title={ch.name}
                          aria-current={isActive ? "page" : undefined}
                          className={`relative whitespace-nowrap px-3.5 py-1.5 text-sm rounded-lg transition-colors duration-150 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B59143]/50 ${isActive
                            ? `${isDark ? 'bg-transparent text-[#FBD282] border border-[#B59143]' : 'bg-transparent text-[#B59143] border border-[#E8C16C]'} after:absolute after:left-2 after:right-2 after:-bottom-1 after:h-0.5 after:rounded-full after:bg-[#B59143]`
                            : `${isDark ? 'bg-transparent text-[#C3C9D3] border border-[gray] hover:text-[#FBD282] hover:bg-[#2A3441]' : 'bg-transparent text-gray-700 border border-gray-200 hover:text-[#B59143] hover:bg-[#FFF7E6]'} focus-visible:bg-[#FFF7E6]`
                            }`}
                        >
                          <span className="max-w-[180px] truncate inline-block align-middle">
                            {ch.name}
                          </span>
                        </button>
                      );
                    });
                  })()}

                  {(() => {
                    const total = selectedProject.channels.length;
                    const baseCap = maxVisibleChannels;
                    const cap = Math.min(baseCap, fitVisibleCount);
                    const needsMore = total > cap;
                    if (!needsMore) return null;
                    const remaining = total - Math.max(0, cap - 1);
                    return (
                      <div className="relative">
                        <Menu>
                          <MenuButton
                            as={Button}
                            size="sm"
                            rightIcon={
                              <IconWrapper
                                icon={FaChevronDown}
                                size={14}
                                className={`${isDark ? 'text-[#C3C9D3] group-hover:text-[#FBD282]' : 'text-gray-500 group-hover:text-[#B59143]'}`}
                              />
                            }
                            className={`px-3.5 py-1.5 rounded-lg ring-1 ${isDark ? 'bg-[#1A1C1F] text-[#C3C9D3] hover:text-[#FBD282] hover:bg-[#2A3441] ring-[#2E2F33]' : 'bg-white text-gray-700 hover:text-[#B59143] hover:bg-[#FFF7E6] ring-gray-200/60'}`}
                            variant="ghost"
                          >
                            {`More (${remaining})`}
                          </MenuButton>
                          <Portal>
                            <MenuList
                              className={`max-h-64 overflow-y-auto z-[20] p-1 rounded-lg ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white border border-gray-200'}`}
                              zIndex={20}
                            >
                              {selectedProject.channels
                                .slice(Math.max(0, cap - 1))
                                .map((ch) => (
                                  <MenuItem
                                    color={isDark ? "white" : "black"}
                                    key={ch.id}
                                    onClick={() => {
                                      setSelectedChannel(ch);
                                      setSelectedTask(null);
                                      setIsViewingGantt(false);
                                      setSearchTerm("");
                                    }}
                                    className={`text-[13px] md:text-[14px] px-3 py-2 rounded-md ${isDark ? 'text-[#FBD282] hover:bg-[#2A3441] hover:text-[#FBD282] focus:bg-[#2A3441]' : 'text-[#B59143] hover:bg-[#FFF7E6] hover:text-[#B59143] focus:bg-[#FFF7E6]'}`}
                                  >
                                    {ch.name}
                                  </MenuItem>
                                ))}
                            </MenuList>
                          </Portal>
                        </Menu>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </Box>
          ) : selectedProject ? (
            <HStack className="space-x-1 mr-6 items-center">
              <Box>
                <Text className={`mt-0 text-sm ${theme.text}`}>
                  No channels available in this project!.
                </Text>
              </Box>
            </HStack>
          ) : null}

          {/* Project selector moved to header above */}
        </Flex>
      )}

      <AlertDialog
        isOpen={isDeleteModalOpen}
        leastDestructiveRef={
          cancelRef as unknown as React.RefObject<HTMLElement>
        }
        onClose={() => setDeleteModalOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent color={color}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color={color}>
              Confirm Delete
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this task? This action cannot be
              undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setDeleteModalOpen(false)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDeleteTask} ml={3} isLoading={isDeleting}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {selectedChannel && selectedProject && (
        <TaskCreator
          bg={bg}
          color={color}
          project={{ id: selectedProject.id, name: selectedProject.name }}
          channel={selectedChannel.id}
          organizationId={organizationId}
          onTaskCreated={handleTaskCreated}
          isOpen={isOpen}
          onClose={onClose}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          setTasks={setTasks}
          selectedTask={selectedTask}
          currentUser={currentUser}
        />
      )}

      {/* Main Content Area: Task List (left) and Charts (right) or Gantt Chart (full width) */}
      <Flex
        minH={0}
        className={`flex-1 overflow-y-auto relative z-0 ${isViewingGantt ? "p-0" : "p-5"
          }`}
      >
        {!selectedChannel ? (
          <Box className="flex-1 flex items-center justify-center  h-[50vh]">
            <div className="w-full  max-w-2xl text-center">
              {/* Simple purple illustration (inline SVG, no external assets) */}
              {/* <div className="mx-auto mb-6 h-40 w-64">
                <svg viewBox="0 0 256 160" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
                  <defs>
                    <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
                      <stop offset="0%" stopColor="#c4b5fd" />
                      <stop offset="100%" stopColor="#a78bfa" />
                    </linearGradient>
                  </defs>
                  <rect x="16" y="24" width="224" height="112" rx="12" fill="#f5f3ff" stroke="#e5e7eb" />
                  <rect x="30" y="40" width="120" height="12" rx="6" fill="url(#g)" />
                  <rect x="30" y="60" width="60" height="10" rx="5" fill="#ddd6fe" />
                  <rect x="30" y="78" width="90" height="10" rx="5" fill="#ddd6fe" />
                  <circle cx="196" cy="80" r="22" fill="#ede9fe" stroke="#ddd6fe" />
                  <path d="M186 80l6 6 14-14" stroke="#8b5cf6" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div> */}
              <h3 className={`text-xl md:text-2xl font-semibold text-[#E8C16C]`}>
                Organize projects, streamline collaboration!
              </h3>
              <p className={`mt-2 text-sm md:text-base ${theme.textMuted}`}>
                Select a project above to view its channels and task groups, and
                start managing your work with ease.
              </p>
            </div>
          </Box>
        ) : isViewingGantt && selectedTask ? (
          <Box
            flex="1"
            bg={isDark ? "#1A1C1F" : "white"}
            color={isDark ? "white" : "#111827"}
            borderWidth="1px"
            borderColor={isDark ? "#2E2F33" : "gray.200"}
            borderRadius="xl"
            p={5}
            overflowX={"auto"}
            width={"80%"}
            overflow="hidden"
          >
            <Gantt
              users={users}
              currentUser={currentUser}
              filteredProjects={filteredprojects}
              bg={bg}
              color={color}
              key={selectedTask.id}
              selectedTask={selectedTask}
              organizationId={organizationId}
              projectId={selectedProject?.id || ""}
              channelId={selectedChannel?.id || ""}
              onExit={() => {
                setIsViewingGantt(false);
                setSelectedTask(null);
              }}
            />
          </Box>
        ) : (
          <Flex className="flex-1" minH={0}>
            {/* Left Column: Task List (now full width) */}
            <Box
              w={{ base: "100%", md: "100%" }}
              pr={{ base: 0, md: 0 }}
              borderRight={{ base: "none", md: "none" }}
              borderColor="gray.300"
              display="flex"
              flexDirection="column"
              minH={0}
            >
              <Box
                className={`rounded-sm px-4 py-0 ${isDark ? 'bg-[#1A1C1F] text-white' : 'bg-white text-black'}`}
                display="flex"
                flexDirection="column"
                flex="1"
                minH={0}
              >
                {/* Task List Header with Search — fixed in place; only the cards area below scrolls */}
                <Flex
                  direction="column"
                  className="mb-2 pt-2"
                  flexShrink={0}
                >
                  <Flex className="flex justify-between items-center mb-2">
                    <Heading
                      className={`text-lg ${isDark ? 'text-white' : 'text-black'}`}
                      style={{ fontWeight: 700, fontSize: "17px" }}
                    >
                      Task Groups in {selectedChannel?.name} :{" "}
                      {filteredTasks?.length > 0 ? filteredTasks?.length : 0}
                    </Heading>
                    <HStack spacing={3}>

                      {canCreateTask && (
                        <Button
                          onClick={onOpen}
                          borderRadius="full"
                          minW={7}
                          h={7}
                          p={0}
                          color="white"
                          bgGradient="linear(to-r, #B59143, #E8C16C)"
                          _hover={{
                            bgGradient: "linear(to-r, #E8C16C, #F4D03F)",
                            transform: "translateY(-1px)",
                          }}
                          className="shadow-sm"
                          aria-label="Add Task Group"
                          title="Add Task Group"
                        >
                          <IconWrapper icon={AiOutlinePlus} size={18} />
                        </Button>
                      )}
                      {/* Status filter */}
                    </HStack>
                  </Flex>

                  <Flex className="mt-1 flex justify-between items-center" gap={3}>
                    <HStack spacing={3} className="flex-1 min-w-0">
                    <Box className="relative w-2/3   md:w-2/5">
                      <InputGroup size="md">
                        <Input
                          type="text"
                          placeholder="Search task groups..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          h="36px"
                          borderRadius="xl"
                          bg={isDark ? "#14161B" : "white"}
                          color={isDark ? "white" : "#111827"}
                          borderColor={isDark ? "#2E2F33" : "gray.200"}
                          fontSize="16px"
                          focusBorderColor="#B59143"
                          _placeholder={{ color: isDark ? "#C3C9D3" : "#9CA3AF" }}
                          _focus={{ boxShadow: "0 0 0 2px rgba(181,145,67,0.35)", borderColor: "#B59143" }}
                          className={`ring-1 transition-all duration-200 ${isDark ? 'ring-[#2E2F33]' : 'ring-gray-200'}`}
                        />
                        <InputLeftElement
                          pointerEvents="none"
                          height="100%"
                          pl={3}
                        >
                          <IconWrapper
                            icon={AiOutlineSearch}
                            size={20}
                            className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-400'}`}
                          />
                        </InputLeftElement>
                        <InputRightElement height="100%" pr={2}>
                          {searchTerm ? (
                            <Button
                              onClick={() => setSearchTerm("")}
                              size="xs"
                              variant="ghost"
                              className="!m-0 !p-0 text-gray-400 hover:text-[#B59143]"
                            >
                              <IconWrapper
                                icon={AiOutlineCloseCircle}
                                size={16}
                              />
                            </Button>
                          ) : null}
                        </InputRightElement>
                      </InputGroup>
                    </Box>
                    <HStack
                      spacing={1}
                      p={1}
                      borderRadius="full"
                      className={`${isDark ? 'bg-[#14161B] border border-[#2E2F33]' : 'bg-gray-100 border border-gray-200'}`}
                    >
                      {(["all", "completed", "pending"] as const).map((key) => {
                        const isActive = groupFilter === key;
                        return (
                          <Button
                            key={key}
                            size="xs"
                            px={4}
                            borderRadius="full"
                            onClick={() => {
                              setGroupFilter(key);
                              // Mutually exclusive with the status dropdown below.
                              if (key !== "all") setStatusFilter("all");
                            }}
                            bgGradient={
                              isActive
                                ? "linear(to-r, #B59143, #E8C16C)"
                                : undefined
                            }
                            bg={isActive ? undefined : "transparent"}
                            color={
                              isActive
                                ? "white"
                                : isDark
                                  ? "#C3C9D3"
                                  : "gray.600"
                            }
                            _hover={{
                              bgGradient: isActive
                                ? "linear(to-r, #E8C16C, #F4D03F)"
                                : undefined,
                              bg: isActive
                                ? undefined
                                : isDark
                                  ? "#2A3441"
                                  : "white",
                            }}
                          >
                            {key === "all"
                              ? "All"
                              : key === "completed"
                                ? "Completed"
                                : "In progress"}
                          </Button>
                        );
                      })}
                    </HStack>
                    </HStack>
                    <Menu>
                      <MenuButton
                        as={Button}
                        colorScheme="yellow"
                        size="xs"
                        px={4}
                        variant="outline"
                        className={`rounded-full z-30 ${isDark ? 'border-[#B59143] text-[#FBD282] hover:bg-[#2A3441]' : 'border-[#E8C16C] text-[#B59143] hover:bg-[#FFF7E6]'}`}
                      >
                        {statusFilter === "all"
                          ? "Filter: All"
                          : `Filter: ${statusFilter}`}
                      </MenuButton>
                      <Portal>
                        <MenuList className={`z-[3000] ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white border border-gray-200'}`} zIndex={3000}>
                          {(
                            [
                              "all",
                              "delay",
                              "In-progress",
                              "Not-started",
                              "completed",
                            ] as const
                          ).map((s) => (
                            <MenuItem
                              color={isDark ? "white" : "black"}
                              fontSize={"14px"}
                              _hover={{ bg: isDark ? "#2A3441" : "#FFF7E6" }}
                              key={s}
                              onClick={() => {
                                setStatusFilter(s);
                                // Mutually exclusive with the Completed/Pending tabs above.
                                if (s !== "all") setGroupFilter("all");
                              }}
                            >
                              {s === "all" ? "All" : s}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Portal>
                    </Menu>
                  </Flex>
                </Flex>

                {/* Scrollable Cards Area */}
                <Box
                  ref={cardsContainerRef}
                  className="relative  my-5 "
                  flex="1"
                  minH={0}
                  overflowY="auto"
                >
                  {/* Task Group Cards Grid */}
                  {filteredTasks.length > 0 ? (
                    <>
                      <Box className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mx-2 ">
                        {currentTasks.map((task) => {
                          // For project-scoped editor access, count only assigned tasks/subtasks
                          const userId = currentUser?.id || currentUser?.uid || "";
                          const filteredData: TaskDataEntry[] = projectScope.canViewAllTasks
                            ? (task.data || [])
                            : filterTaskDataByAssignment(task.data, userId) as TaskDataEntry[];
                          
                          const mainCount = filteredData.length;
                          const subCount =
                            filteredData.reduce(
                              (acc: number, entry: TaskDataEntry) =>
                                acc + (entry.subtasks?.length || 0),
                              0
                            ) || 0;
                          const totalInGroup = mainCount + subCount;
                          const leftBorderColor =
                            task.id.charCodeAt(0) % 3 === 0
                              ? "#a855f7"
                              : task.id.charCodeAt(0) % 3 === 1
                                ? "#3b82f6"
                                : "#10b981";

                          // Calculate status counts for this task group (using filtered data)
                          const statusCounts = filteredData.reduce(
                            (acc: { delayed: number; completed: number; inProgress: number; notStarted: number }, entry: TaskDataEntry) => {
                              // Count main task status
                              if (entry.status === "delay") acc.delayed++;
                              else if (entry.status === "completed")
                                acc.completed++;
                              else if (entry.status === "In-progress")
                                acc.inProgress++;
                              else if (entry.status === "Not-started")
                                acc.notStarted++;

                              // Count subtask statuses
                              entry.subtasks?.forEach((subtask) => {
                                if (subtask.status === "delay") acc.delayed++;
                                else if (subtask.status === "completed")
                                  acc.completed++;
                                else if (subtask.status === "In-progress")
                                  acc.inProgress++;
                                else if (subtask.status === "Not-started")
                                  acc.notStarted++;
                              });

                              return acc;
                            },
                            {
                              delayed: 0,
                              completed: 0,
                              inProgress: 0,
                              notStarted: 0,
                            }
                          );
                          return (
                            <Box
                              key={task.id}
                              className={`group relative rounded-2xl h-full border-2 transition-all duration-200 hover:shadow-lg ${isDark
                                ? 'bg-gradient-to-br from-[#0B0B0B] to-[#1A1A1A] text-white border-[#2E2F33] hover:border-[#B59143]'
                                : 'bg-gradient-to-br from-[#FFFDFB] to-[#FFFCF7] text-black border-[#E1C16C] hover:border-[#B59143]'
                                }`}
                            >
                              <Box className="px-5 py-3 h-full min-h-[150px]">
                                {/* Header: Title + Edit/Delete at top-right */}
                                <Flex className="items-start justify-between">
                                  <Tooltip label={task.title} hasArrow placement="top" openDelay={300}>
                                    <Heading
                                      size="sm"
                                      className={`text-[13px] font-semibold leading-snug tracking-tight pr-2 ${isDark ? 'text-white' : 'text-gray-900'}`}
                                      style={{
                                        fontWeight: 600,
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        fontSize: "14px",
                                      }}
                                    >
                                      {task.title}
                                    </Heading>
                                  </Tooltip>
                                  {/* Edit & Delete at top-right */}
                                  <HStack spacing={0} flexShrink={0}>
                                    {canEditTask(task) && (
                                      <IconButton
                                        icon={
                                          <IconWrapper
                                            icon={CiEdit}
                                            size={16}
                                            className={`${isDark ? 'text-[#C3C9D3] group-hover:text-[#FBD282]' : 'text-gray-600 group-hover:text-[#B59143]'}`}
                                          />
                                        }
                                        aria-label="Edit Task"
                                        size="xs"
                                        variant="ghost"
                                        color="gray.600"
                                        _hover={{
                                          bg: "transparent",
                                          color: isDark ? "#FBD282" : "#B59143",
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleUpdateTask(task);
                                        }}
                                      />
                                    )}
                                    {canDelete(task) && (
                                      <IconButton
                                        icon={
                                          <IconWrapper
                                            icon={MdDeleteOutline}
                                            size={16}
                                            className="text-gray-600 group-hover:text-red-600"
                                          />
                                        }
                                        aria-label="Delete Task"
                                        size="xs"
                                        variant="ghost"
                                        color="gray.600"
                                        _hover={{
                                          bg: "transparent",
                                          color: "red.600",
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setTaskToDelete(task.id);
                                          setDeleteModalOpen(true);
                                        }}
                                      />
                                    )}
                                  </HStack>
                                </Flex>

                                {/* Description */}
                                <Tooltip label={task.description?.trim() || "No description"} hasArrow placement="top" openDelay={300}>
                                  <Text
                                    className={`mt-1.5 text-[13px] ${isDark ? 'text-[#C3C9D3]' : 'text-gray-700'}`}
                                    style={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {task.description?.trim() || "No description"}
                                  </Text>
                                </Tooltip>

                                {/* Status Chips */}
                                <Flex className="mt-3 flex-wrap gap-1.5">
                                  {(() => {
                                    // If a filter is active, show only that chip
                                    if (statusFilter !== "all") {
                                      const keyMap: Record<
                                        string,
                                        {
                                          count: number;
                                          label: string;
                                          cls: string;
                                        }
                                      > = {
                                        delay: {
                                          count: statusCounts.delayed,
                                          label: "delayed",
                                          cls: "bg-white border border-red-700 text-red-700",
                                        },
                                        completed: {
                                          count: statusCounts.completed,
                                          label: "completed",
                                          cls: "bg-white border border-green-700 text-green-700",
                                        },
                                        "In-progress": {
                                          count: statusCounts.inProgress,
                                          label: "in progress",
                                          cls: "bg-white border border-blue-700 text-blue-700",
                                        },
                                        "Not-started": {
                                          count: statusCounts.notStarted,
                                          label: "not started",
                                          cls: "bg-white border border-gray-700 text-gray-700",
                                        },
                                        all: { count: 0, label: "", cls: "" },
                                      };
                                      const e = keyMap[statusFilter];
                                      return e && e.count > 0 ? (
                                        <Box
                                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium ${e.cls}`}
                                        >
                                          {e.count} {e.label}
                                        </Box>
                                      ) : null;
                                    }

                                    const entries = [
                                      {
                                        key: "delay",
                                        count: statusCounts.delayed,
                                        label: "Delayed",
                                        cls: "bg-white border border-red-700 text-red-700",
                                      },
                                      {
                                        key: "completed",
                                        count: statusCounts.completed,
                                        label: "Completed",
                                        cls: "bg-white border border-green-700 text-green-700",
                                      },
                                      {
                                        key: "inProgress",
                                        count: statusCounts.inProgress,
                                        label: "In Progress",
                                        cls: "bg-white border border-blue-700 text-blue-700",
                                      },
                                      {
                                        key: "notStarted",
                                        count: statusCounts.notStarted,
                                        label: "Not Started",
                                        cls: "bg-white border border-gray-700 text-gray-700",
                                      },
                                    ];
                                    // Priority: delay > completed > the rest by count
                                    const rank: Record<string, number> = {
                                      delay: 4000,
                                      completed: 3000,
                                      inProgress: 2000,
                                      notStarted: 1000,
                                    };
                                    const topTwo = entries
                                      .filter((e) => e.count > 0)
                                      .sort(
                                        (a, b) =>
                                          b.count +
                                          rank[b.key] -
                                          (a.count + rank[a.key])
                                      )
                                      .slice(0, 2);
                                    return (
                                      <>
                                        {topTwo.map((e) => (
                                          <Box
                                            key={e.key}
                                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium ${e.cls}`}
                                          >
                                            {e.count} {e.label}
                                          </Box>
                                        ))}
                                      </>
                                    );
                                  })()}
                                </Flex>

                                {/* Bottom Row: View tasks (left) + Task count (right) */}
                                <Flex className="mt-2 items-center justify-between">
                                  <Button
                                    size="xs"
                                    variant="ghost"
                                    className={`underline px-2 ${isDark ? 'text-[#FBD282] hover:text-[#F4D03F] hover:bg-[#2A3441]' : 'text-[#B59143] hover:text-[#E8C16C] hover:bg-[#FFF7E6]'}`}
                                    onClick={() => {
                                      setSelectedTask(task);
                                      setIsViewingGantt(true);
                                    }}
                                    color={isDark ? "white" : "#B59143"}
                                  >
                                    View tasks
                                  </Button>

                                  {/* Task count badge at bottom-right */}
                                  <Box className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium shadow-inner ${isDark ? 'bg-[#2A3441] border border-[#2E2F33] text-[#FBD282]' : 'bg-gray-100 border border-gray-200 text-gray-700'}`}>
                                    {totalInGroup} tasks
                                  </Box>
                                </Flex>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>

                    </>
                  ) : filteredTasks.length === 0 && searchTerm !== "" ? (
                    <Text
                      textAlign="center"
                      fontSize={"sm"}
                      color="gray.500"
                      opacity={0.8}
                      pt={10}
                    >
                      No tasks found matching your search.
                    </Text>
                  ) : (
                    <Text
                      textAlign="center"
                      fontSize={"md"}
                      color={color}
                      opacity={0.8}
                      height={"30vh"}
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"center"}
                    >
                      {canCreateTask
                        ? "No tasks available in this channel. Click New Task (+) to add one!."
                        : projectScope.canViewAllTasks
                        ? "No tasks available in this channel."
                        : "No tasks have been assigned to you in this channel."}
                    </Text>
                  )}
                </Box>
              </Box>
            </Box>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default GanttMain;
